import React, { useEffect, useState, useRef } from 'react';
import API from '../api';
import { toast } from 'react-toastify';
import { usePharmacy } from '../context/PharmacyContext';

const SalesPage = () => {
  const getCache = (key, defaultVal) => {
    const cached = sessionStorage.getItem(`salesPage_${key}`);
    return cached ? JSON.parse(cached) : defaultVal;
  };

  const [sales, setSales] = useState(() => getCache('sales', []));
  const [search, setSearch] = useState(() => getCache('search', ''));
  const [filterType, setFilterType] = useState(() => getCache('filterType', 'ALL'));
  const [dateFrom, setDateFrom] = useState(() => getCache('dateFrom', ''));
  const [dateTo, setDateTo] = useState(() => getCache('dateTo', ''));
  const [selectedSale, setSelectedSale] = useState(null);
  const [loading, setLoading] = useState(false);

  const [showPrintPanel, setShowPrintPanel] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printOptions, setPrintOptions] = useState(() => getCache('printOptions', {
    showDetails: false,
    showSummary: true,
    orientation: 'portrait'
  }));
  const printPanelRef = useRef(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const { pharmacyDetails } = usePharmacy();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (printPanelRef.current && !printPanelRef.current.contains(event.target)) {
        setShowPrintPanel(false);
      }
    };
    if (showPrintPanel) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPrintPanel]);

  useEffect(() => {
    fetchSales();
  }, []);

  useEffect(() => {
    sessionStorage.setItem('salesPage_search', JSON.stringify(search));
  }, [search]);

  useEffect(() => {
    sessionStorage.setItem('salesPage_filterType', JSON.stringify(filterType));
  }, [filterType]);

  useEffect(() => {
    sessionStorage.setItem('salesPage_dateFrom', JSON.stringify(dateFrom));
  }, [dateFrom]);

  useEffect(() => {
    sessionStorage.setItem('salesPage_dateTo', JSON.stringify(dateTo));
  }, [dateTo]);

  useEffect(() => {
    sessionStorage.setItem('salesPage_printOptions', JSON.stringify(printOptions));
  }, [printOptions]);

  const fetchSales = async () => {
    try {
      if (!sessionStorage.getItem('salesPage_sales')) setLoading(true);
      const res = await API.get('/sales');
      const data = Array.isArray(res.data) ? res.data : [];
      setSales(data);
      sessionStorage.setItem('salesPage_sales', JSON.stringify(data));
    } catch (err) {
      console.error(err);
      toast.error('Failed to load sales');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (saleId) => {
    if (!window.confirm('Are you sure you want to delete this bill?')) return;

    try {
      await API.delete(`/sales/${saleId}`);
      const newSales = sales.filter(s => s._id !== saleId);
      setSales(newSales);
      sessionStorage.setItem('salesPage_sales', JSON.stringify(newSales));
      toast.success('Sale deleted successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete sale');
    }
  };

  const fmt = (num) =>
    Number.isFinite(Number(num))
      ? Number(num).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : '0.00';

  const [viewingModalSale, setViewingModalSale] = useState(null);

  const applyDatePreset = (preset) => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];

    if (preset === 'TODAY') {
      setDateFrom(todayStr);
      setDateTo(todayStr);
    } else if (preset === 'YESTERDAY') {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const yestStr = yesterday.toISOString().split('T')[0];
      setDateFrom(yestStr);
      setDateTo(yestStr);
    } else if (preset === 'THIS_MONTH') {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
      setDateFrom(firstDay);
      setDateTo(todayStr);
    } else if (preset === 'ALL') {
      setDateFrom('');
      setDateTo('');
    }
  };

  const filteredSales = React.useMemo(() => sales.filter(s => {
    const searchLower = search.toLowerCase();
    const matchesSearch = (s.billNumber || '').toLowerCase().includes(searchLower) ||
      (s.partyName || '').toLowerCase().includes(searchLower) ||
      (s.party || '').toLowerCase().includes(searchLower) ||
      (s.patientName || '').toLowerCase().includes(searchLower) ||
      (s.doctorName || '').toLowerCase().includes(searchLower);
    const matchesType = filterType === 'ALL' || s.paymentType === filterType;

    let matchesDate = true;
    if (dateFrom || dateTo) {
      const saleDate = new Date(s.createdAt);
      if (dateFrom) matchesDate = matchesDate && saleDate >= new Date(dateFrom);
      if (dateTo) matchesDate = matchesDate && saleDate <= new Date(dateTo + 'T23:59:59');
    }

    return matchesSearch && matchesType && matchesDate;
  }), [sales, search, filterType, dateFrom, dateTo]);

  const sortedSales = React.useMemo(() =>
    [...filteredSales].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [filteredSales]
  );

  const {
    totalSales, totalDiscount, cashSales, onlineSales, returnSales,
    cashTotal, onlineTotal, returnTotal
  } = React.useMemo(() => {
    let totalSales = 0, totalDiscount = 0, cashTotal = 0, onlineTotal = 0, returnTotal = 0;
    let cashSales = 0, onlineSales = 0, returnSales = 0;
    for (const s of filteredSales) {
      totalSales += s.totalAmount || 0;
      totalDiscount += s.discount || 0;
      if (s.paymentType === 'CASH') { cashSales++; cashTotal += s.totalAmount || 0; }
      else if (s.paymentType === 'ONLINE') { onlineSales++; onlineTotal += s.totalAmount || 0; }
      else if (s.paymentType === 'RETURN') { returnSales++; returnTotal += s.totalAmount || 0; }
    }
    return { totalSales, totalDiscount, cashSales, onlineSales, returnSales, cashTotal, onlineTotal, returnTotal };
  }, [filteredSales]);

  const groupSalesByDate = (salesList) => {
    const groups = {};
    salesList.forEach(sale => {
      const dateKey = new Date(sale.createdAt).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      });
      if (!groups[dateKey]) {
        groups[dateKey] = {
          sales: [],
          total: 0,
          discount: 0,
          cash: 0,
          online: 0,
          return: 0
        };
      }
      groups[dateKey].sales.push(sale);
      groups[dateKey].total += sale.totalAmount || 0;
      groups[dateKey].discount += sale.discount || 0;
      if (sale.paymentType === 'CASH') groups[dateKey].cash += sale.totalAmount || 0;
      if (sale.paymentType === 'ONLINE') groups[dateKey].online += sale.totalAmount || 0;
      if (sale.paymentType === 'RETURN') groups[dateKey].return += sale.totalAmount || 0;
    });
    return groups;
  };

  const handlePrint = (sale) => {
    const printWindow = window.open('', '_blank');
    const itemsList = ((sale.items || sale.products) || [])
      .map(i => `
        <tr>
          <td>${i.name || '-'}</td>
          <td style="text-align: center;">${i.quantity || 0} ${i.quantityUnit || ''}</td>
          <td style="text-align: right;">Rs ${(i.price || 0).toFixed(2)}</td>
          <td style="text-align: right; font-weight: 600;">Rs ${((i.price || 0) * (i.quantity || 0)).toFixed(2)}</td>
        </tr>
      `)
      .join('');

    const subtotal = (sale.totalAmount || 0) + (sale.discount || 0);
    const balance = (sale.totalAmount || 0) - (sale.paidAmount || 0);

    let contactInfo = '';
    if (pharmacyDetails.phone) contactInfo += `Phone: ${pharmacyDetails.phone}`;
    if (pharmacyDetails.email) contactInfo += ` | Email: ${pharmacyDetails.email}`;

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice - ${sale.billNumber}</title>
        <style>
          @page { size: auto; margin: 5mm; } 
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Helvetica', 'Arial', sans-serif; 
            padding: 10px; 
            color: #1a202c;
            font-size: 11px;
            line-height: 1.3;
            max-width: 100%;
          }
          .header { 
            text-align: center; 
            border-bottom: 2px solid #1880c2; 
            padding-bottom: 5px; 
            margin-bottom: 10px; 
          }
          .header h1 { 
            margin: 0; 
            color: #1880c2; 
            font-size: 20px;
            font-weight: 800;
            text-transform: uppercase;
          }
          .header p { margin: 1px 0; color: #4a5568; font-size: 10px; }
          .header .pan { font-weight: 700; color: #1a202c; }
          .invoice-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 10px;
            margin-bottom: 10px;
          }
          .info-box {
            background: #f4f9fd;
            padding: 8px;
            border-radius: 4px;
            border: 1px solid #e0f2fe;
          }
          .info-row { display: flex; justify-content: space-between; margin-bottom: 2px; }
          .info-label { font-size: 9px; color: #718096; font-weight: 600; text-transform: uppercase; }
          .info-val { font-size: 11px; font-weight: 700; color: #1a202c; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
          thead tr { background: #1880c2; }
          th { 
            background: #1880c2; color: white; 
            padding: 6px; 
            text-align: left; 
            font-weight: 600; font-size: 10px; text-transform: uppercase;
          }
          td { 
            padding: 6px; 
            border-bottom: 1px solid #e1e8ed; font-size: 11px; 
          }
          tbody tr:last-child td { border-bottom: none; }
          .totals-section {
            display: flex;
            justify-content: flex-end;
            margin-top: 5px;
            margin-bottom: 15px;
          }
          .totals-table { width: 220px; border-collapse: collapse; margin-bottom: 0; }
          .totals-table td { padding: 3px 0; text-align: right; border: none; }
          .t-label { color: #4a5568; font-size: 10px; font-weight: 600; padding-right: 10px; }
          .t-val { font-size: 11px; font-weight: 700; color: #1a202c; }
          .grand-total { border-top: 1px solid #cbd5e1 !important; padding-top: 5px !important; margin-top: 2px; }
          .grand-total .t-label { color: #1880c2; font-size: 12px; }
          .grand-total .t-val { color: #1880c2; font-size: 14px; }
          .footer { 
            margin-top: 15px; 
            text-align: center; 
            color: #718096; 
            font-size: 9px; 
            border-top: 1px solid #e1e8ed; 
            padding-top: 5px; 
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${pharmacyDetails.name}</h1>
          ${contactInfo ? `<p>${contactInfo}</p>` : ''}
          ${pharmacyDetails.pan ? `<p class="pan">PAN: ${pharmacyDetails.pan}</p>` : ''}
        </div>
        <div class="invoice-grid">
          <div class="info-box">
            <div class="info-row"><span class="info-label">Invoice No</span><span class="info-val">${sale.billNumber || 'N/A'}</span></div>
            <div class="info-row"><span class="info-label">Date</span><span class="info-val">${new Date(sale.createdAt).toLocaleDateString('en-IN')}</span></div>
            <div class="info-row"><span class="info-label">Payment</span><span class="info-val">${sale.paymentType}</span></div>
          </div>
          <div class="info-box">
            <div class="info-row"><span class="info-label">Customer</span><span class="info-val">${sale.party || 'Walk-in Customer'}</span></div>
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th width="50%">Item</th>
              <th width="15%" style="text-align: center;">Qty</th>
              <th width="15%" style="text-align: right;">Price</th>
              <th width="20%" style="text-align: right;">Total</th>
            </tr>
          </thead>
          <tbody>${itemsList}</tbody>
        </table>
        <div class="totals-section">
          <table class="totals-table">
            <tr><td class="t-label">Subtotal</td><td class="t-val">Rs ${subtotal.toFixed(2)}</td></tr>
            ${sale.discount > 0 ? `<tr><td class="t-label">Discount</td><td class="t-val" style="color:#dc2626">- Rs ${(sale.discount || 0).toFixed(2)}</td></tr>` : ''}
            <tr class="grand-total"><td class="t-label">Grand Total</td><td class="t-val">Rs ${(sale.totalAmount || 0).toFixed(2)}</td></tr>
            <tr><td class="t-label">Paid</td><td class="t-val">Rs ${(sale.paidAmount || 0).toFixed(2)}</td></tr>
            ${Math.abs(balance) > 0.01 ? `<tr><td class="t-label">${balance > 0 ? 'Balance Due' : 'Change'}</td><td class="t-val">Rs ${Math.abs(balance).toFixed(2)}</td></tr>` : ''}
          </table>
        </div>
        <div class="footer">
          <p>Thank you for your business!</p>
          <p>Printed: ${new Date().toLocaleString('en-IN')}</p>
        </div>
      </body>
      </html>
    `;
    printWindow.document.write(printContent);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 250);
  };

  const handleQuickPrintAll = () => {
    setIsPrinting(true);
    setTimeout(() => {
      handlePrintAllSales();
      setIsPrinting(false);
      setShowPrintPanel(false);
    }, 300);
  };

  const handlePrintAllSales = () => {
    const groupedSales = groupSalesByDate(sortedSales);
    const sortedDates = Object.keys(groupedSales).sort((a, b) => {
      const dateA = new Date(groupedSales[a].sales[0].createdAt);
      const dateB = new Date(groupedSales[b].sales[0].createdAt);
      return dateB - dateA;
    });

    const printWindow = window.open('', '_blank');

    let dateRangeText = 'All Time';
    if (dateFrom && dateTo) {
      dateRangeText = `${new Date(dateFrom).toLocaleDateString('en-IN')} - ${new Date(dateTo).toLocaleDateString('en-IN')}`;
    } else if (dateFrom) {
      dateRangeText = `From ${new Date(dateFrom).toLocaleDateString('en-IN')}`;
    } else if (dateTo) {
      dateRangeText = `Until ${new Date(dateTo).toLocaleDateString('en-IN')}`;
    }

    let filterText = filterType === 'ALL' ? 'All Payment Types' : filterType;
    if (search) filterText += ` | Search: "${search}"`;

    let dailySectionsHTML = '';
    sortedDates.forEach((dateKey, dateIndex) => {
      const group = groupedSales[dateKey];

      let salesRowsHTML = '';
      group.sales.forEach((sale, saleIndex) => {
        const items = sale.items || sale.products || [];

        salesRowsHTML += `
          <tr class="${saleIndex % 2 === 0 ? 'even-row' : 'odd-row'}">
            <td class="cell">${saleIndex + 1}</td>
            <td class="cell"><strong>${sale.billNumber || 'N/A'}</strong></td>
            <td class="cell">${new Date(sale.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</td>
            <td class="cell">${sale.party || 'Walk-in Customer'}</td>
            <td class="cell center">
              <span class="payment-badge ${sale.paymentType.toLowerCase()}">${sale.paymentType}</span>
            </td>
            <td class="cell center">${items.length} items</td>
            ${printOptions.showDetails ? `
              <td class="cell right discount">${sale.discount > 0 ? `-Rs ${fmt(sale.discount)}` : '-'}</td>
            ` : ''}
            <td class="cell right amount"><strong>Rs ${fmt(sale.totalAmount)}</strong></td>
          </tr>
          ${printOptions.showDetails ? `
            <tr class="details-row">
              <td colspan="${printOptions.showDetails ? 8 : 7}" class="items-cell">
                <div class="items-list">
                  ${items.map(i => `<span class="item-tag">${i.name} (${i.quantity} x Rs ${fmt(i.price)})</span>`).join('')}
                </div>
              </td>
            </tr>
          ` : ''}
        `;
      });

      dailySectionsHTML += `
        <div class="date-section">
          <div class="date-header">
            <div class="date-title">Date: ${dateKey}</div>
            <div class="date-stats">
              <span>Cash: Rs ${fmt(group.cash)}</span>
              <span>Online: Rs ${fmt(group.online)}</span>
              ${group.return > 0 ? `<span>Return: Rs ${fmt(group.return)}</span>` : ''}
              <span class="date-total">Total: Rs ${fmt(group.total)}</span>
            </div>
          </div>
          
          <table class="sales-table">
            <thead>
              <tr>
                <th class="th" width="4%">#</th>
                <th class="th" width="14%">Invoice</th>
                <th class="th" width="8%">Time</th>
                <th class="th" width="22%">Customer</th>
                <th class="th center" width="10%">Payment</th>
                <th class="th center" width="10%">Items</th>
                ${printOptions.showDetails ? `<th class="th right" width="12%">Discount</th>` : ''}
                <th class="th right" width="14%">Amount</th>
              </tr>
            </thead>
            <tbody>${salesRowsHTML}</tbody>
          </table>
          
          <div class="daily-footer">
            <span>Sales: ${group.sales.length}</span>
            <span class="day-total">Day Total: <strong>Rs ${fmt(group.total)}</strong></span>
          </div>
        </div>
      `;
    });

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Sales Report - ${pharmacyDetails.name}</title>
        <style>
          @page { size: ${printOptions.orientation === 'landscape' ? 'A4 landscape' : 'A4 portrait'}; margin: 10mm; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            color: #1a202c;
            font-size: 10px;
            line-height: 1.4;
            background: #fff;
          }

          .report-header {
            text-align: center;
            padding-bottom: 15px;
            border-bottom: 3px solid #1880c2;
            margin-bottom: 20px;
          }

          .pharmacy-name {
            font-size: 24px;
            font-weight: 800;
            color: #1880c2;
            text-transform: uppercase;
            margin-bottom: 4px;
          }

          .pharmacy-info { color: #4a5568; font-size: 10px; margin: 2px 0; }

          .report-title {
            font-size: 16px;
            font-weight: 700;
            color: #1a202c;
            margin-top: 10px;
            padding: 6px 16px;
            background: #f4f9fd;
            display: inline-block;
            border-radius: 16px;
            border: 1px solid #e0f2fe;
          }

          .report-meta {
            display: flex;
            justify-content: center;
            gap: 20px;
            margin-top: 10px;
            font-size: 10px;
            color: #718096;
          }

          .meta-value { font-weight: 700; color: #1a202c; }

          ${printOptions.showSummary ? `
          .summary-section {
            display: grid;
            grid-template-columns: repeat(5, 1fr);
            gap: 10px;
            margin-bottom: 20px;
            padding: 15px;
            background: #f4f9fd;
            border-radius: 10px;
            border: 1px solid #e0f2fe;
          }

          .summary-card {
            text-align: center;
            padding: 10px;
            background: white;
            border-radius: 8px;
            border: 1px solid #e0f2fe;
          }

          .summary-value { font-size: 14px; font-weight: 800; margin-bottom: 2px; }
          .summary-label { font-size: 8px; color: #718096; text-transform: uppercase; }
          .summary-card.total .summary-value { color: #1880c2; }
          .summary-card.cash .summary-value { color: #16a34a; }
          .summary-card.online .summary-value { color: #2563eb; }
          .summary-card.return .summary-value { color: #dc2626; }
          .summary-card.discount .summary-value { color: #d97706; }
          ` : ''}

          .date-section {
            margin-bottom: 20px;
            background: white;
            border-radius: 10px;
            border: 1px solid #e0f2fe;
            overflow: hidden;
            page-break-inside: avoid;
          }

          .date-header {
            background: #1880c2;
            padding: 10px 16px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            gap: 8px;
          }

          .date-title {
            color: white;
            font-size: 12px;
            font-weight: 700;
          }

          .date-stats {
            display: flex;
            gap: 12px;
            color: rgba(255,255,255,0.9);
            font-size: 10px;
          }

          .date-total {
            background: white;
            color: #1880c2;
            padding: 3px 10px;
            border-radius: 12px;
            font-weight: 700;
          }

          .sales-table { width: 100%; border-collapse: collapse; }

          .th {
            padding: 8px 10px;
            text-align: left;
            font-size: 8px;
            font-weight: 700;
            color: #4a5568;
            background: #f4f9fd;
            text-transform: uppercase;
            border-bottom: 2px solid #e0f2fe;
          }

          .th.center { text-align: center; }
          .th.right { text-align: right; }

          .cell {
            padding: 8px 10px;
            font-size: 10px;
            color: #1a202c;
            border-bottom: 1px solid #f1f5f9;
          }

          .cell.center { text-align: center; }
          .cell.right { text-align: right; }
          .cell.discount { color: #dc2626; }

          .even-row { background: #fff; }
          .odd-row { background: #fafafa; }

          .payment-badge {
            display: inline-block;
            padding: 2px 8px;
            border-radius: 10px;
            font-size: 8px;
            font-weight: 700;
            text-transform: uppercase;
          }

          .payment-badge.cash { background: #dcfce7; color: #16a34a; border: 1px solid #bbf7d0; }
          .payment-badge.online { background: #dbeafe; color: #2563eb; border: 1px solid #bfdbfe; }
          .payment-badge.return { background: #fee2e2; color: #dc2626; border: 1px solid #fecaca; }

          .details-row { background: #f8fafc; }
          .items-cell { padding: 6px 10px !important; border-bottom: 1px solid #e1e8ed !important; }
          .items-list { display: flex; flex-wrap: wrap; gap: 4px; }
          .item-tag {
            background: white;
            border: 1px solid #e1e8ed;
            padding: 2px 6px;
            border-radius: 4px;
            font-size: 8px;
            color: #4a5568;
          }

          .daily-footer {
            display: flex;
            justify-content: space-between;
            padding: 8px 16px;
            background: #f4f9fd;
            border-top: 1px solid #e0f2fe;
            font-size: 10px;
            color: #4a5568;
          }

          .day-total { color: #1880c2; }
          .day-total strong { font-size: 12px; }

          .grand-total-section {
            margin-top: 20px;
            padding: 20px;
            background: #1880c2;
            border-radius: 12px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            gap: 16px;
          }

          .grand-stats { display: flex; gap: 24px; }
          .grand-stat { text-align: center; }
          .grand-stat-value { font-size: 18px; font-weight: 800; color: white; }
          .grand-stat-label { font-size: 9px; color: rgba(255,255,255,0.8); text-transform: uppercase; }

          .grand-total-box { text-align: right; }
          .grand-total-label { font-size: 10px; color: rgba(255,255,255,0.8); margin-bottom: 2px; }
          .grand-total-amount { font-size: 28px; font-weight: 800; color: white; }

          .report-footer {
            margin-top: 24px;
            padding-top: 12px;
            border-top: 1px dashed #e0f2fe;
            text-align: center;
            color: #718096;
            font-size: 9px;
          }

          .signature-section {
            display: flex;
            justify-content: space-between;
            margin-top: 30px;
            padding: 0 40px;
          }

          .signature-box { text-align: center; width: 140px; }
          .signature-line {
            border-top: 1px solid #cbd5e1;
            padding-top: 6px;
            font-size: 9px;
            color: #4a5568;
          }

          @media print {
            body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
            .date-section { break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="report-header">
          <div class="pharmacy-name">${pharmacyDetails.name}</div>
          ${pharmacyDetails.address ? `<div class="pharmacy-info">${pharmacyDetails.address}</div>` : ''}
          ${pharmacyDetails.phone ? `<div class="pharmacy-info">Phone: ${pharmacyDetails.phone} ${pharmacyDetails.email ? `| Email: ${pharmacyDetails.email}` : ''}</div>` : ''}
          ${pharmacyDetails.pan ? `<div class="pharmacy-info"><strong>PAN: ${pharmacyDetails.pan}</strong></div>` : ''}
          <div class="report-title">Sales Report</div>
          <div class="report-meta">
            <span>Date: <span class="meta-value">${dateRangeText}</span></span>
            <span>Filter: <span class="meta-value">${filterText}</span></span>
            <span>Generated: <span class="meta-value">${new Date().toLocaleString('en-IN')}</span></span>
          </div>
        </div>

        ${printOptions.showSummary ? `
        <div class="summary-section">
          <div class="summary-card total">
            <div class="summary-value">Rs ${fmt(totalSales)}</div>
            <div class="summary-label">Total Sales</div>
          </div>
          <div class="summary-card">
            <div class="summary-value">${filteredSales.length}</div>
            <div class="summary-label">Transactions</div>
          </div>
          <div class="summary-card cash">
            <div class="summary-value">Rs ${fmt(cashTotal)}</div>
            <div class="summary-label">${cashSales} Cash</div>
          </div>
          <div class="summary-card online">
            <div class="summary-value">Rs ${fmt(onlineTotal)}</div>
            <div class="summary-label">${onlineSales} Online</div>
          </div>
          <div class="summary-card discount">
            <div class="summary-value">Rs ${fmt(totalDiscount)}</div>
            <div class="summary-label">Discounts</div>
          </div>
        </div>
        ` : ''}

        ${dailySectionsHTML}

        <div class="grand-total-section">
          <div class="grand-stats">
            <div class="grand-stat">
              <div class="grand-stat-value">${filteredSales.length}</div>
              <div class="grand-stat-label">Total Sales</div>
            </div>
            <div class="grand-stat">
              <div class="grand-stat-value">${sortedDates.length}</div>
              <div class="grand-stat-label">Days</div>
            </div>
          </div>
          <div class="grand-total-box">
            <div class="grand-total-label">GRAND TOTAL</div>
            <div class="grand-total-amount">Rs ${fmt(totalSales)}</div>
          </div>
        </div>

        <div class="report-footer">
          <p>Generated on ${new Date().toLocaleString('en-IN')}</p>
          <div class="signature-section">
            <div class="signature-box"><div class="signature-line">Prepared By</div></div>
            <div class="signature-box"><div class="signature-line">Verified By</div></div>
            <div class="signature-box"><div class="signature-line">Approved By</div></div>
          </div>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 300);
  };

  const clearFilters = () => {
    setSearch('');
    setFilterType('ALL');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters = search || filterType !== 'ALL' || dateFrom || dateTo;

  const styles = {
    container: { minHeight: '100vh', background: '#f4f9fd', fontFamily: "'Inter', sans-serif" },
    header: {
      background: '#ffffff',
      borderBottom: '1px solid #e0f2fe',
      padding: isMobile ? '16px' : '20px 32px',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'center',
      gap: isMobile ? 16 : 0,
      position: 'sticky',
      top: 0,
      zIndex: 100,
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)'
    },
    headerTitle: { display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 12 },
    headerIcon: {
      width: isMobile ? 36 : 40,
      height: isMobile ? 36 : 40,
      background: '#1880c2',
      borderRadius: 6,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: '0.5px'
    },
    headerText: { fontSize: isMobile ? 18 : 20, fontWeight: 700, color: '#1a202c', letterSpacing: '-0.3px' },
    headerSubtext: { fontSize: 12, color: '#718096', fontWeight: 500, marginTop: 2 },
    headerActions: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      width: isMobile ? '100%' : 'auto',
      flexWrap: 'wrap',
      position: 'relative'
    },
    main: { maxWidth: 1400, margin: '0 auto', padding: isMobile ? '16px' : '24px 32px' },
    statsRow: {
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)',
      gap: 16,
      marginBottom: 24
    },
    statCard: {
      background: '#ffffff',
      borderRadius: isMobile ? 8 : 10,
      padding: isMobile ? 16 : 20,
      border: '1px solid #e0f2fe',
      display: 'flex',
      gap: 12,
      alignItems: 'center',
      boxShadow: '0 1px 2px rgba(24, 128, 194, 0.05)'
    },
    statIconBox: {
      width: isMobile ? 40 : 48,
      height: isMobile ? 40 : 48,
      borderRadius: 8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 12,
      flexShrink: 0,
      fontWeight: 700,
      letterSpacing: '0.5px'
    },
    statContent: { flex: 1, minWidth: 0 },
    statNumber: { fontSize: isMobile ? 16 : 20, fontWeight: 800, color: '#1a202c' },
    statTitle: { fontSize: 10, fontWeight: 600, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 2 },
    statHint: { fontSize: 11, color: '#94a3b8', marginTop: 2 },
    card: {
      background: '#ffffff',
      borderRadius: isMobile ? 8 : 10,
      border: '1px solid #e0f2fe',
      marginBottom: isMobile ? 16 : 24,
      boxShadow: '0 1px 2px rgba(24, 128, 194, 0.05)'
    },
    cardHeader: {
      padding: '16px 24px',
      borderBottom: '1px solid #e0f2fe',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 12,
      background: '#f8fafc'
    },
    cardTitle: {
      fontSize: 15,
      fontWeight: 700,
      color: '#1a202c',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      margin: 0
    },
    cardBody: { padding: isMobile ? 16 : 24 },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
      gap: 16
    },
    formGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
    label: { fontSize: 11, fontWeight: 600, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' },
    input: {
      padding: '10px 14px',
      borderRadius: 6,
      border: '1px solid #e1e8ed',
      fontSize: 14,
      width: '100%',
      boxSizing: 'border-box',
      background: '#fff',
      outline: 'none',
      transition: 'border-color 0.2s'
    },
    select: {
      padding: '10px 14px',
      borderRadius: 6,
      border: '1px solid #e1e8ed',
      fontSize: 14,
      width: '100%',
      boxSizing: 'border-box',
      background: '#fff',
      cursor: 'pointer',
      outline: 'none'
    },
    btnPrimary: {
      padding: '10px 20px',
      background: '#1880c2',
      color: 'white',
      border: 'none',
      borderRadius: 6,
      fontWeight: 600,
      cursor: 'pointer',
      fontSize: 14,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      transition: 'all 0.2s'
    },
    btnSecondary: {
      padding: '10px 20px',
      background: '#ffffff',
      color: '#4a5568',
      border: '1px solid #e0f2fe',
      borderRadius: 6,
      fontWeight: 600,
      cursor: 'pointer',
      fontSize: 14,
      transition: 'all 0.2s'
    },
    badge: {
      padding: '4px 10px',
      borderRadius: 4,
      fontSize: 11,
      fontWeight: 600,
      display: 'inline-block',
      letterSpacing: '0.3px'
    },
    badgeCash: { background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' },
    badgeOnline: { background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' },
    badgeReturn: { background: '#fef5f5', color: '#991b1b', border: '1px solid #fecaca' },
    actionBtn: {
      padding: '6px 12px',
      borderRadius: 6,
      border: '1px solid',
      fontSize: 12,
      fontWeight: 600,
      cursor: 'pointer',
      marginRight: 8,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      transition: 'all 0.2s'
    },
    printBtn: { background: '#f4f9fd', color: '#1880c2', borderColor: '#e0f2fe' },
    deleteBtn: { background: '#fef5f5', color: '#991b1b', borderColor: '#fecaca' },
    emptyState: { textAlign: 'center', padding: isMobile ? 40 : 60, color: '#718096' },
    saleCard: {
      background: '#fff',
      borderRadius: 8,
      border: '1px solid #e0f2fe',
      padding: isMobile ? 16 : 20,
      marginBottom: 12,
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    saleCardSelected: {
      borderColor: '#1880c2',
      boxShadow: '0 0 0 3px rgba(24, 128, 194, 0.1)'
    },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: {
      padding: '12px 16px',
      textAlign: 'left',
      fontSize: 11,
      fontWeight: 600,
      color: '#718096',
      background: '#f4f9fd',
      borderBottom: '1px solid #e0f2fe',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    td: {
      padding: '12px 16px',
      fontSize: 13,
      color: '#1a202c',
      borderBottom: '1px solid #f4f9fd'
    },
    skeleton: {
      background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.5s infinite',
      borderRadius: 6
    },
    printPanelContainer: {
      position: 'relative'
    },
    printPanel: {
      position: 'absolute',
      top: 'calc(100% + 8px)',
      right: 0,
      width: 320,
      background: '#fff',
      borderRadius: 10,
      boxShadow: '0 10px 25px rgba(24, 128, 194, 0.15)',
      border: '1px solid #e0f2fe',
      zIndex: 1000,
      overflow: 'hidden',
      animation: 'slideDown 0.2s ease'
    },
    printPanelHeader: {
      padding: '16px 20px',
      background: '#f8fafc',
      borderBottom: '1px solid #e0f2fe'
    },
    printPanelTitle: {
      fontSize: 14,
      fontWeight: 700,
      color: '#1a202c',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: 8
    },
    printPanelSubtitle: {
      fontSize: 12,
      color: '#718096',
      marginTop: 4
    },
    printPanelBody: {
      padding: 16
    },
    printOption: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '12px 14px',
      borderRadius: 8,
      marginBottom: 8,
      cursor: 'pointer',
      transition: 'all 0.2s',
      border: '1px solid #e0f2fe',
      background: '#fff'
    },
    printOptionActive: {
      borderColor: '#1880c2',
      background: '#f4f9fd'
    },
    printOptionIcon: {
      width: 36,
      height: 36,
      borderRadius: 6,
      background: '#e0f2fe',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 12,
      fontWeight: 700,
      color: '#1880c2'
    },
    printOptionText: {
      flex: 1
    },
    printOptionTitle: {
      fontSize: 13,
      fontWeight: 600,
      color: '#1a202c'
    },
    printOptionDesc: {
      fontSize: 11,
      color: '#718096'
    },
    checkbox: {
      width: 16,
      height: 16,
      accentColor: '#1880c2',
      cursor: 'pointer'
    },
    printPanelStats: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: 8,
      padding: '12px 0',
      borderTop: '1px solid #e0f2fe',
      marginTop: 8
    },
    printPanelStat: {
      textAlign: 'center',
      padding: 8,
      background: '#f8fafc',
      borderRadius: 6,
      border: '1px solid #e0f2fe'
    },
    printPanelStatValue: {
      fontSize: 14,
      fontWeight: 700,
      color: '#1880c2'
    },
    printPanelStatLabel: {
      fontSize: 9,
      color: '#718096',
      marginTop: 2,
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    printPanelFooter: {
      padding: '12px 16px',
      background: '#f8fafc',
      borderTop: '1px solid #e0f2fe',
      display: 'flex',
      gap: 10
    },
    printBtnPrimary: {
      flex: 1,
      padding: '10px 16px',
      background: '#1880c2',
      color: 'white',
      border: 'none',
      borderRadius: 6,
      fontWeight: 600,
      fontSize: 13,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      transition: 'all 0.2s'
    },
    printBtnSecondary: {
      padding: '10px 16px',
      background: 'white',
      color: '#4a5568',
      border: '1px solid #e0f2fe',
      borderRadius: 6,
      fontWeight: 600,
      fontSize: 13,
      cursor: 'pointer',
      transition: 'all 0.2s'
    }
  };

  const SkeletonStatCard = () => (
    <div style={styles.statCard}>
      <div style={{ ...styles.skeleton, width: 40, height: 40, borderRadius: 8 }}></div>
      <div style={{ flex: 1 }}>
        <div style={{ ...styles.skeleton, height: 14, width: '60%', marginBottom: 8 }}></div>
        <div style={{ ...styles.skeleton, height: 20, width: '80%' }}></div>
      </div>
    </div>
  );

  const SkeletonSaleCard = () => (
    <div style={styles.saleCard}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ ...styles.skeleton, height: 20, width: 120 }}></div>
            <div style={{ ...styles.skeleton, height: 20, width: 60, borderRadius: 4 }}></div>
          </div>
          <div style={{ ...styles.skeleton, height: 12, width: '70%' }}></div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ ...styles.skeleton, height: 24, width: 100, marginBottom: 10, marginLeft: 'auto' }}></div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <div style={{ ...styles.skeleton, height: 28, width: 60, borderRadius: 4 }}></div>
            <div style={{ ...styles.skeleton, height: 28, width: 60, borderRadius: 4 }}></div>
          </div>
        </div>
      </div>
    </div>
  );

  const PaymentBadge = ({ type }) => {
    const badgeStyles = { CASH: styles.badgeCash, ONLINE: styles.badgeOnline, RETURN: styles.badgeReturn };
    return <span style={{ ...styles.badge, ...badgeStyles[type] }}>{type}</span>;
  };

  const SaleCard = ({ sale, isSelected, onClick }) => {
    const items = sale.items || sale.products || [];
    const balance = (sale.totalAmount || 0) - (sale.paidAmount || 0);

    return (
      <div
        style={{ ...styles.saleCard, ...(isSelected ? styles.saleCardSelected : {}) }}
        onClick={onClick}
        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#f8fafc'; }}
        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = '#fff'; }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#1a202c' }}>{sale.billNumber || 'N/A'}</span>
              <PaymentBadge type={sale.paymentType} />
            </div>
            <div style={{ fontSize: 12, color: '#718096' }}>
              {sale.party || 'Walk-in Customer'} • {new Date(sale.createdAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })} • {new Date(sale.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
          <div style={{ textAlign: isMobile ? 'left' : 'right' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#1880c2', marginBottom: 10 }}>Rs {fmt(sale.totalAmount || 0)}</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: isMobile ? 'flex-start' : 'flex-end' }} onClick={(e) => e.stopPropagation()}>
              <button
                style={{ ...styles.actionBtn, background: '#eff6ff', color: '#1e40af', borderColor: '#bfdbfe' }}
                onClick={() => setViewingModalSale(sale)}
              >
                View Items
              </button>
              <button
                style={{ ...styles.actionBtn, ...styles.printBtn }}
                onClick={() => handlePrint(sale)}
                onMouseEnter={(e) => e.currentTarget.style.background = '#e0f2fe'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#f4f9fd'}
              >
                Print
              </button>
              <button
                style={{ ...styles.actionBtn, ...styles.deleteBtn }}
                onClick={() => handleDelete(sale._id)}
                onMouseEnter={(e) => e.currentTarget.style.background = '#fee2e2'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#fef5f5'}
              >
                Delete
              </button>
            </div>
          </div>
        </div>

        {isSelected && (
          <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #e0f2fe' }}>
            <h4 style={{ fontSize: 13, fontWeight: 700, color: '#1a202c', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Items Details
            </h4>

            <div style={{ overflowX: 'auto', margin: '0 -16px', padding: '0 16px' }}>
              <table style={{ ...styles.table, minWidth: 500 }}>
                <thead>
                  <tr>
                    <th style={styles.th}>Item</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Qty</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Unit</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Price</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => (
                    <tr key={idx}>
                      <td style={{ ...styles.td, fontWeight: 600 }}>{item.name || '-'}</td>
                      <td style={{ ...styles.td, textAlign: 'center', fontWeight: 700, color: '#1880c2' }}>{item.quantity || 0}</td>
                      <td style={{ ...styles.td, textAlign: 'center', color: '#718096', fontSize: 12 }}>{item.quantityUnit || 'units'}</td>
                      <td style={{ ...styles.td, textAlign: 'right', color: '#718096' }}>Rs {fmt(item.price || 0)}</td>
                      <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700, color: '#1880c2' }}>Rs {fmt((item.price || 0) * (item.quantity || 0))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #e0f2fe', display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'stretch' : 'flex-end', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, width: '100%', maxWidth: isMobile ? 'none' : 280 }}>
                <span style={{ color: '#718096', fontWeight: 600, fontSize: 13 }}>Subtotal:</span>
                <span style={{ fontWeight: 700, color: '#1a202c', fontSize: 13 }}>Rs {fmt((sale.totalAmount || 0) + (sale.discount || 0))}</span>
              </div>
              {sale.discount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, width: '100%', maxWidth: isMobile ? 'none' : 280 }}>
                  <span style={{ color: '#718096', fontWeight: 600, fontSize: 13 }}>Discount:</span>
                  <span style={{ fontWeight: 700, color: '#dc2626', fontSize: 13 }}>- Rs {fmt(sale.discount || 0)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, width: '100%', maxWidth: isMobile ? 'none' : 280, paddingTop: 10, borderTop: '1px dashed #cbd5e1', marginTop: 4 }}>
                <span style={{ color: '#1880c2', fontWeight: 700, fontSize: 15 }}>Total:</span>
                <span style={{ fontWeight: 700, color: '#1880c2', fontSize: 15 }}>Rs {fmt(sale.totalAmount || 0)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, width: '100%', maxWidth: isMobile ? 'none' : 280 }}>
                <span style={{ color: '#718096', fontWeight: 600, fontSize: 13 }}>Paid:</span>
                <span style={{ fontWeight: 700, color: '#16a34a', fontSize: 13 }}>Rs {fmt(sale.paidAmount || 0)}</span>
              </div>
              {Math.abs(balance) > 0.01 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, width: '100%', maxWidth: isMobile ? 'none' : 280 }}>
                  <span style={{ color: '#718096', fontWeight: 600, fontSize: 13 }}>{balance > 0 ? 'Balance Due:' : 'Change:'}</span>
                  <span style={{ fontWeight: 700, color: balance > 0 ? '#d97706' : '#16a34a', fontSize: 13 }}>Rs {fmt(Math.abs(balance))}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const PrintPanel = () => {
    const groupedSales = groupSalesByDate(sortedSales);
    const daysCount = Object.keys(groupedSales).length;

    return (
      <div style={styles.printPanel} ref={printPanelRef}>
        <div style={styles.printPanelHeader}>
          <h3 style={styles.printPanelTitle}>
            Sales Report
          </h3>
          <p style={styles.printPanelSubtitle}>
            {filteredSales.length} sales • {daysCount} day{daysCount !== 1 ? 's' : ''}
          </p>
        </div>

        <div style={styles.printPanelBody}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button
              onClick={() => setPrintOptions({ ...printOptions, orientation: 'portrait' })}
              style={{
                flex: 1,
                padding: '8px',
                borderRadius: 6,
                border: printOptions.orientation === 'portrait' ? '1px solid #1880c2' : '1px solid #e1e8ed',
                background: printOptions.orientation === 'portrait' ? '#f4f9fd' : '#fff',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                color: printOptions.orientation === 'portrait' ? '#1880c2' : '#718096',
                transition: 'all 0.2s'
              }}
            >
              Portrait
            </button>
            <button
              onClick={() => setPrintOptions({ ...printOptions, orientation: 'landscape' })}
              style={{
                flex: 1,
                padding: '8px',
                borderRadius: 6,
                border: printOptions.orientation === 'landscape' ? '1px solid #1880c2' : '1px solid #e1e8ed',
                background: printOptions.orientation === 'landscape' ? '#f4f9fd' : '#fff',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                color: printOptions.orientation === 'landscape' ? '#1880c2' : '#718096',
                transition: 'all 0.2s'
              }}
            >
              Landscape
            </button>
          </div>

          <div
            style={{ ...styles.printOption, ...(printOptions.showSummary ? styles.printOptionActive : {}) }}
            onClick={() => setPrintOptions({ ...printOptions, showSummary: !printOptions.showSummary })}
          >
            <div style={{ ...styles.printOptionIcon, background: printOptions.showSummary ? '#e0f2fe' : '#f1f5f9' }}>SUM</div>
            <div style={styles.printOptionText}>
              <div style={styles.printOptionTitle}>Summary Cards</div>
              <div style={styles.printOptionDesc}>Show totals overview</div>
            </div>
            <input type="checkbox" checked={printOptions.showSummary} onChange={() => { }} style={styles.checkbox} />
          </div>

          <div
            style={{ ...styles.printOption, ...(printOptions.showDetails ? styles.printOptionActive : {}) }}
            onClick={() => setPrintOptions({ ...printOptions, showDetails: !printOptions.showDetails })}
          >
            <div style={{ ...styles.printOptionIcon, background: printOptions.showDetails ? '#e0f2fe' : '#f1f5f9' }}>DTL</div>
            <div style={styles.printOptionText}>
              <div style={styles.printOptionTitle}>Item Details</div>
              <div style={styles.printOptionDesc}>Include sold items</div>
            </div>
            <input type="checkbox" checked={printOptions.showDetails} onChange={() => { }} style={styles.checkbox} />
          </div>

          <div style={styles.printPanelStats}>
            <div style={styles.printPanelStat}>
              <div style={styles.printPanelStatValue}>{filteredSales.length}</div>
              <div style={styles.printPanelStatLabel}>Sales</div>
            </div>
            <div style={styles.printPanelStat}>
              <div style={styles.printPanelStatValue}>{daysCount}</div>
              <div style={styles.printPanelStatLabel}>Days</div>
            </div>
            <div style={styles.printPanelStat}>
              <div style={{ ...styles.printPanelStatValue, fontSize: 12 }}>Rs {fmt(totalSales)}</div>
              <div style={styles.printPanelStatLabel}>Total</div>
            </div>
          </div>
        </div>

        <div style={styles.printPanelFooter}>
          <button
            style={styles.printBtnSecondary}
            onClick={() => setShowPrintPanel(false)}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
          >
            Cancel
          </button>
          <button
            style={{ ...styles.printBtnPrimary, opacity: isPrinting ? 0.7 : 1 }}
            onClick={handleQuickPrintAll}
            disabled={isPrinting}
            onMouseEnter={(e) => !isPrinting && (e.currentTarget.style.background = '#0369a1')}
            onMouseLeave={(e) => !isPrinting && (e.currentTarget.style.background = '#1880c2')}
          >
            {isPrinting ? 'Preparing...' : 'Print Report'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div style={styles.container}>
        <header style={styles.header}>
          <div style={styles.headerTitle}>
            <div style={styles.headerIcon}>SAL</div>
            <div>
              <div style={styles.headerText}>Sales History</div>
              <div style={styles.headerSubtext}>View and manage all transactions</div>
            </div>
          </div>
          <div style={styles.headerActions}>
            <div style={styles.printPanelContainer} ref={printPanelRef}>
              <button
                style={styles.btnSecondary}
                onClick={() => setShowPrintPanel(!showPrintPanel)}
                disabled={filteredSales.length === 0}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f4f9fd'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#ffffff'}
              >
                Print Report
                <span style={{
                  marginLeft: 4,
                  transform: showPrintPanel ? 'rotate(180deg)' : 'rotate(0)',
                  transition: 'transform 0.2s',
                  display: 'inline-block',
                  fontSize: 10
                }}>▼</span>
              </button>
              {showPrintPanel && <PrintPanel />}
            </div>

            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 12px',
              background: '#e0f2fe',
              color: '#1880c2',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              border: '1px solid #bae6fd'
            }}>
              {filteredSales.length} Sales
            </div>
          </div>
        </header>

        <main style={styles.main}>
          <div style={styles.statsRow}>
            {loading ? (
              <>
                <SkeletonStatCard />
                <SkeletonStatCard />
                <SkeletonStatCard />
                <SkeletonStatCard />
                {!isMobile && <SkeletonStatCard />}
              </>
            ) : (
              <>
                <div style={styles.statCard}>
                  <div style={{ ...styles.statIconBox, background: '#e0f2fe', color: '#1880c2' }}>TOT</div>
                  <div style={styles.statContent}>
                    <div style={{ ...styles.statNumber, color: '#1880c2' }}>Rs {fmt(totalSales)}</div>
                    <div style={styles.statTitle}>Total Sales</div>
                    <div style={styles.statHint}>{filteredSales.length} transactions</div>
                  </div>
                </div>

                <div style={styles.statCard}>
                  <div style={{ ...styles.statIconBox, background: '#fef5f5', color: '#991b1b' }}>DSC</div>
                  <div style={styles.statContent}>
                    <div style={{ ...styles.statNumber, color: '#dc2626' }}>Rs {fmt(totalDiscount)}</div>
                    <div style={styles.statTitle}>Total Discount</div>
                    <div style={styles.statHint}>Savings given</div>
                  </div>
                </div>

                <div style={styles.statCard}>
                  <div style={{ ...styles.statIconBox, background: '#f0fdf4', color: '#166534' }}>CSH</div>
                  <div style={styles.statContent}>
                    <div style={{ ...styles.statNumber, color: '#16a34a' }}>{cashSales}</div>
                    <div style={styles.statTitle}>Cash Sales</div>
                    <div style={styles.statHint}>Rs {fmt(cashTotal)}</div>
                  </div>
                </div>

                <div style={styles.statCard}>
                  <div style={{ ...styles.statIconBox, background: '#eff6ff', color: '#1e40af' }}>ONL</div>
                  <div style={styles.statContent}>
                    <div style={{ ...styles.statNumber, color: '#2563eb' }}>{onlineSales}</div>
                    <div style={styles.statTitle}>Online Sales</div>
                    <div style={styles.statHint}>Rs {fmt(onlineTotal)}</div>
                  </div>
                </div>

                {returnSales > 0 && (
                  <div style={styles.statCard}>
                    <div style={{ ...styles.statIconBox, background: '#fef5f1', color: '#9a3412' }}>RET</div>
                    <div style={styles.statContent}>
                      <div style={{ ...styles.statNumber, color: '#d97706' }}>{returnSales}</div>
                      <div style={styles.statTitle}>Returns</div>
                      <div style={styles.statHint}>Rs {fmt(returnTotal)}</div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>Search & Filter</h3>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={() => applyDatePreset('TODAY')}
                  style={{ padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid #cbd5e1', background: '#f8fafc', color: '#1e293b', cursor: 'pointer' }}
                >
                  Today
                </button>
                <button
                  type="button"
                  onClick={() => applyDatePreset('YESTERDAY')}
                  style={{ padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid #cbd5e1', background: '#f8fafc', color: '#1e293b', cursor: 'pointer' }}
                >
                  Yesterday
                </button>
                <button
                  type="button"
                  onClick={() => applyDatePreset('THIS_MONTH')}
                  style={{ padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid #cbd5e1', background: '#f8fafc', color: '#1e293b', cursor: 'pointer' }}
                >
                  This Month
                </button>
                <button
                  type="button"
                  onClick={() => applyDatePreset('ALL')}
                  style={{ padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid #cbd5e1', background: '#f8fafc', color: '#1e293b', cursor: 'pointer' }}
                >
                  All Time
                </button>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    style={{ ...styles.btnSecondary, padding: '6px 12px', fontSize: 12 }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f4f9fd'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#ffffff'}
                  >
                    Clear All
                  </button>
                )}
              </div>
            </div>
            <div style={styles.cardBody}>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Search</label>
                  <input
                    type="text"
                    placeholder="Search bill, customer, patient, doctor..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={styles.input}
                    onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                    onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Payment Type</label>
                  <select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={styles.select} onFocus={(e) => e.target.style.borderColor = '#1880c2'} onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}>
                    <option value="ALL">All Types</option>
                    <option value="CASH">Cash</option>
                    <option value="ONLINE">Online</option>
                    <option value="RETURN">Return</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Date From</label>
                  <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={styles.input} onFocus={(e) => e.target.style.borderColor = '#1880c2'} onBlur={(e) => e.target.style.borderColor = '#e1e8ed'} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Date To</label>
                  <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={styles.input} onFocus={(e) => e.target.style.borderColor = '#1880c2'} onBlur={(e) => e.target.style.borderColor = '#e1e8ed'} />
                </div>
              </div>
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>All Sales</h3>
            </div>

            {loading ? (
              <div style={{ padding: isMobile ? 16 : 24 }}>
                {[1, 2, 3, 4, 5].map(i => <SkeletonSaleCard key={i} />)}
              </div>
            ) : filteredSales.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>📄</div>
                <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1a202c', margin: '0 0 8px' }}>No sales found</h3>
                <p style={{ margin: 0, fontSize: 14 }}>Try adjusting your filters or create a new sale</p>
              </div>
            ) : (
              <div style={{ padding: isMobile ? 16 : 24 }}>
                {sortedSales.map(s => (
                  <SaleCard
                    key={s._id}
                    sale={s}
                    isSelected={selectedSale?._id === s._id}
                    onClick={() => setSelectedSale(selectedSale?._id === s._id ? null : s)}
                  />
                ))}
              </div>
            )}

            {filteredSales.length > 0 && !loading && (
              <div style={{
                padding: '16px 24px',
                background: '#f8fafc',
                borderTop: '1px solid #e0f2fe',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 8
              }}>
                <p style={{ margin: 0, fontSize: 13, color: '#718096' }}>
                  Showing <strong style={{ color: '#1a202c' }}>{filteredSales.length}</strong> sales
                </p>
                <p style={{ margin: 0, fontSize: 12, color: '#94a3b8' }}>Click on any sale to view details</p>
              </div>
            )}
          </div>
        </main>
      </div>

      {viewingModalSale && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setViewingModalSale(null)}>
          <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 650, maxHeight: '90vh', overflowY: 'auto', padding: 24, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, borderBottom: '1px solid #e0f2fe', paddingBottom: 12 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, color: '#1e293b' }}>Invoice Details: {viewingModalSale.billNumber}</h3>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                  Date: {new Date(viewingModalSale.createdAt).toLocaleString('en-IN')} | Payment: {viewingModalSale.paymentType}
                </div>
              </div>
              <button onClick={() => setViewingModalSale(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>

            <div style={{ background: '#f4f9fd', borderRadius: 8, padding: 12, marginBottom: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13, border: '1px solid #e0f2fe' }}>
              <div><strong>Customer / Party:</strong> {viewingModalSale.partyName || viewingModalSale.party || 'Walk-in Customer'}</div>
              <div><strong>Patient Name:</strong> {viewingModalSale.patientName || 'N/A'}</div>
              <div><strong>Doctor Name:</strong> {viewingModalSale.doctorName || 'N/A'}</div>
              <div><strong>Status:</strong> {viewingModalSale.paymentType}</div>
            </div>

            <h4 style={{ fontSize: 14, margin: '0 0 10px', color: '#1e293b' }}>Purchased Items ({viewingModalSale.items?.length || 0})</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
              <thead>
                <tr style={{ background: '#1880c2', color: '#fff' }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 12 }}>Item</th>
                  <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 12 }}>Qty</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: 12 }}>Price</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: 12 }}>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {(viewingModalSale.items || viewingModalSale.products || []).map((item, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #e0f2fe' }}>
                    <td style={{ padding: '8px 12px', fontSize: 13, fontWeight: 600 }}>{item.name}</td>
                    <td style={{ padding: '8px 12px', fontSize: 13, textAlign: 'center' }}>{item.quantity} {item.quantityUnit || ''}</td>
                    <td style={{ padding: '8px 12px', fontSize: 13, textAlign: 'right' }}>Rs {fmt(item.price)}</td>
                    <td style={{ padding: '8px 12px', fontSize: 13, textAlign: 'right', fontWeight: 600, color: '#1880c2' }}>Rs {fmt((item.price || 0) * (item.quantity || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 12, borderTop: '1px solid #e0f2fe' }}>
              <div style={{ fontSize: 13 }}>
                <div>Subtotal: Rs {fmt((viewingModalSale.totalAmount || 0) + (viewingModalSale.discount || 0))}</div>
                {viewingModalSale.discount > 0 && <div style={{ color: '#dc2626' }}>Discount: -Rs {fmt(viewingModalSale.discount)}</div>}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#1880c2' }}>Grand Total: Rs {fmt(viewingModalSale.totalAmount)}</div>
                <div style={{ fontSize: 12, color: '#16a34a' }}>Paid: Rs {fmt(viewingModalSale.paidAmount || viewingModalSale.totalAmount)}</div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button onClick={() => handlePrint(viewingModalSale)} style={{ padding: '8px 16px', background: '#1880c2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>Print Receipt</button>
              <button onClick={() => setViewingModalSale(null)} style={{ padding: '8px 16px', background: '#e0f2fe', color: '#1e293b', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SalesPage;